import React, {useEffect, useMemo, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useParams} from 'react-router-dom';
import ModalContainer from '../../uiToolkit/ModalContainer';
import Button from '../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../uiToolkit/LoadingSpinner/LoadingSpinner';
import LearnersTable from '../LearnersTable/LearnersTable';
import LearnersEmpty from '../LearnersTable/LearnersEmpty';
import LearnersSearch from '../LearnersTable/LearnersSearch';
import LearnerGroupsStore from './store/LearnerGroupsStore';
import {GroupMember, LearnerGroup} from '../../data/models/Groups/LearnerGroup';
import {
  LearnerListItem,
  LearnerStatus,
} from '../../data/models/Learners/Learner';
import {useDebouncedSearch} from '../../utils/useDebouncedSearch';
import {learnerGroupsText} from './learnerGroupsText';
import styles from './GroupMembersModal.module.css';

interface Props {
  group: LearnerGroup;
  onClose: () => void;
  learnerGroupsStore?: LearnerGroupsStore;
}

const MEMBERS_TAB = 'members';
const ADD_TAB = 'add';
const PAGE_SIZE = 10;

const GroupMembersModal = inject('learnerGroupsStore')(
  observer(({group, onClose, learnerGroupsStore}: Props) => {
    const {organizationId} = useParams();

    const [tab, setTab] = useState(MEMBERS_TAB);
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [learners, setLearners] = useState<LearnerListItem[]>([]);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [checked, setChecked] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [fetchKey, setFetchKey] = useState(0);

    const debouncedSearch = useDebouncedSearch(search);
    const isMembersTab = tab === MEMBERS_TAB;

    useEffect(() => {
      if (!organizationId) return;
      let active = true;
      setLoading(true);
      Promise.all([
        learnerGroupsStore!.getGroupMembers(organizationId, group.id),
        learnerGroupsStore!.getAllWorkspaceLearners(organizationId),
      ]).then(([groupMembers, workspace]) => {
        if (!active) return;
        setMembers(groupMembers);
        setLearners(
          workspace.filter(learner => learner.status === LearnerStatus.ACTIVE),
        );
        setLoading(false);
      });
      return () => {
        active = false;
      };
    }, [organizationId, group.id, fetchKey]);

    useEffect(() => {
      setPage(1);
      setChecked([]);
    }, [debouncedSearch, tab]);

    const memberIds = useMemo(
      () => new Set(members.map(member => member.learnerId)),
      [members],
    );

    const filtered = useMemo(() => {
      const query = debouncedSearch.trim().toLowerCase();
      return learners
        .filter(learner =>
          isMembersTab
            ? memberIds.has(learner.learnerId)
            : !memberIds.has(learner.learnerId),
        )
        .filter(
          learner =>
            !query ||
            learner.name.toLowerCase().includes(query) ||
            learner.email.toLowerCase().includes(query),
        );
    }, [learners, memberIds, isMembersTab, debouncedSearch]);

    const pages = Math.ceil(filtered.length / PAGE_SIZE);
    const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const onToggle = (id: string) => {
      setChecked(current =>
        current.includes(id)
          ? current.filter(item => item !== id)
          : [...current, id],
      );
    };

    const onToggleAll = () => {
      const allIds = rows.map(row => row.learnerId);
      const allChecked =
        allIds.length > 0 && allIds.every(id => checked.includes(id));
      setChecked(current =>
        allChecked
          ? current.filter(id => !allIds.includes(id))
          : Array.from(new Set([...current, ...allIds])),
      );
    };

    const switchTab = (next: string) => {
      setTab(next);
      setSearch('');
      setChecked([]);
      setPage(1);
    };

    const onConfirm = async () => {
      if (!organizationId || !checked.length || submitting) return;
      setSubmitting(true);
      const success = isMembersTab
        ? await learnerGroupsStore!.removeLearners(
            organizationId,
            group.id,
            checked,
          )
        : await learnerGroupsStore!.addLearners(
            organizationId,
            group.id,
            checked,
          );
      setSubmitting(false);
      setChecked([]);
      if (success) {
        if (isMembersTab) {
          setPage(1);
        } else {
          switchTab(MEMBERS_TAB);
        }
        setFetchKey(key => key + 1);
      }
    };

    const confirmTitle = `${
      isMembersTab
        ? learnerGroupsText.removeSelected
        : learnerGroupsText.addSelected
    }${checked.length ? ` (${checked.length})` : ''}`;

    const renderBody = () => {
      if (loading) {
        return (
          <div className={styles.loading}>
            <LoadingSpinner size={48} />
          </div>
        );
      }
      if (!rows.length) {
        if (debouncedSearch) {
          return <LearnersEmpty title={learnerGroupsText.cantFindLearners} />;
        }
        return isMembersTab ? (
          <LearnersEmpty
            title={learnerGroupsText.emptyMembersTitle}
            text={learnerGroupsText.emptyMembersText}
            actionTitle={learnerGroupsText.addLearners}
            onAction={() => switchTab(ADD_TAB)}
          />
        ) : (
          <LearnersEmpty
            title={learnerGroupsText.emptyCandidatesTitle}
            text={learnerGroupsText.emptyCandidatesText}
          />
        );
      }
      return (
        <LearnersTable
          learners={rows}
          checked={checked}
          page={page}
          pages={pages}
          onToggle={onToggle}
          onToggleAll={onToggleAll}
          onPageChange={setPage}
          footerLabel={
            isMembersTab
              ? learnerGroupsText.membersCount(filtered.length)
              : learnerGroupsText.learnersCount(filtered.length)
          }
        />
      );
    };

    return (
      <ModalContainer onClose={onClose} className={styles.modal}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h2 className={styles.title}>
              {learnerGroupsText.membersModalTitle(group.name)}
            </h2>
            <p className={styles.subtitle}>
              {learnerGroupsText.membersModalSubtitle}
            </p>
          </div>

          <div className={styles.tabsRow}>
            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={isMembersTab}
                className={`${styles.tab} ${
                  isMembersTab ? styles.tabActive : ''
                }`}
                onClick={() => switchTab(MEMBERS_TAB)}>
                {learnerGroupsText.membersTab}
                <span className={styles.badge}>{members.length}</span>
              </button>
            </div>
            <div className={styles.tabsActions}>
              <LearnersSearch value={search} onChange={setSearch} />
              {isMembersTab && (
                <Button
                  title={learnerGroupsText.addLearners}
                  uiType="action"
                  onClick={() => switchTab(ADD_TAB)}
                />
              )}
            </div>
          </div>

          <div className={styles.body}>{renderBody()}</div>

          <div className={styles.footer}>
            <span className={styles.status}>
              {checked.length
                ? learnerGroupsText.selectedCount(checked.length)
                : learnerGroupsText.membersCount(members.length)}
            </span>
            <div className={styles.actions}>
              <Button
                title={learnerGroupsText.close}
                uiType="text"
                onClick={onClose}
              />
              <Button
                title={confirmTitle}
                uiType={isMembersTab ? 'action_danger' : 'action'}
                onClick={onConfirm}
                disabled={!checked.length}
                isLoading={submitting}
              />
            </div>
          </div>
        </div>
      </ModalContainer>
    );
  }),
);

export default GroupMembersModal;
